import { useState, useCallback } from 'react'
import axios from 'axios'
import { checkBookingExistence } from 'repositories/ReceptionApi/checkBookingExistence'
import { patchBookingWorkRecord } from 'repositories/ReceptionApi/patchBookingWorkRecord'
import { formatBookingNo } from 'utils/formatters'
import { BookingWorkRecordEntryFinishStatus } from '../type'
import { ERROR_CODES } from 'repositories/errorCodes'

const DEFAULT_ERROR_MESSAGE = '電話番号が合致しません。または受付データが存在しません。'

export interface WorkRecordResult {
  status: BookingWorkRecordEntryFinishStatus
  hyphenatedBookingNumber: string
  finishedAt: string | null
}

/**
 * 作業打刻の2段階 API 呼び出しを行うフック
 * ① checkBookingExistence で予約存在チェック（完了後にブッキング情報が返却される）
 * ② patchBookingWorkRecord で作業打刻実行（更新後のブッキング情報が返却される）
 */
export const useBookingWorkRecord = (tenantId: string) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [isSubmitError, setIsSubmitError] = useState(false)
  const [errorMessage, setErrorMessage] = useState(DEFAULT_ERROR_MESSAGE)
  const [workRecordResult, setWorkRecordResult] = useState<WorkRecordResult | null>(null)

  const clearSubmitError = useCallback(() => {
    setIsSubmitError(false)
    setErrorMessage(DEFAULT_ERROR_MESSAGE)
  }, [])

  const submitBookingWorkRecord = useCallback(
    async (
      phoneNumber: string,
      callback?: {
        onSuccess?: () => void
      }
    ): Promise<boolean> => {
      setLoading(true)
      setError(null)
      setIsSubmitError(false)
      setErrorMessage(DEFAULT_ERROR_MESSAGE)

      try {
        // ① 予約存在チェック
        const checkResult = await checkBookingExistence({
          tenantId,
          driverPhone: phoneNumber,
        })

        if (!checkResult.data.exists) {
          setIsSubmitError(true)
          return false
        }

        // ② 作業打刻実行（更新後のブッキング情報が返却される）
        const bookingId = checkResult.data.booking!.bookingId
        const patchResult = await patchBookingWorkRecord(bookingId)
        const { data } = patchResult

        // ブッキング情報からステータスを取得(ステータスは完了 or 途中のどちらかのはず)
        const status = data.booking.status as BookingWorkRecordEntryFinishStatus
        if (!status) {
          setIsSubmitError(true)
          return false
        }

        setWorkRecordResult({
          status,
          hyphenatedBookingNumber: formatBookingNo(data.booking.bookingNo),
          finishedAt: data.booking.finishedAt || null,
        })

        if (callback?.onSuccess) {
          callback.onSuccess()
        }

        return true
      } catch (err) {
        setError(err as Error)
        setIsSubmitError(true)

        if (axios.isAxiosError(err) && err.response?.data) {
          const errors = err.response.data.data?.errors
          if (Array.isArray(errors)) {
            const conflictError = errors.find(
              (e: { code: string }) => e.code === ERROR_CODES.WORK_LOG_CONFLICT_CODE
              )
            if (conflictError?.message) {
              setErrorMessage(conflictError.message)
              return false
            }
          }
        }

        setErrorMessage(DEFAULT_ERROR_MESSAGE)
        return false
      } finally {
        setLoading(false)
      }
    },
    [tenantId]
  )

  return {
    workRecordResult,
    loading,
    error,
    isSubmitError,
    errorMessage,
    submitBookingWorkRecord,
    clearSubmitError,
  }
}
